// Content-shaped skeleton loaders. Replaces the spinner-on-mount on every
// patient-portal page so the perceived load feels instant. CSS-only — no
// dependency on a skeleton library.
//
// Three building blocks:
//   <SkeletonLine /> single text line
//   <SkeletonBlock /> rectangular block (cards, avatars)
//   <SkeletonCard />  pre-composed card-shaped skeleton (most common pattern)
//
// All shimmer animations respect `prefers-reduced-motion` — the bg flips to
// a static colour instead.

import React from 'react'
import styled, { keyframes, css } from 'styled-components'

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`

const baseSkeleton = css`
  background: linear-gradient(
    90deg,
    ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'} 0%,
    ${(p) => p.theme?.color?.border || '#E6E0D6'} 40%,
    ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'} 80%
  );
  background-size: 200px 100%;
  background-repeat: no-repeat;
  border-radius: 6px;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

export const SkeletonLine = styled.div`
  ${baseSkeleton};
  height: ${(p) => p.$h || '12px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '8px'};
`

export const SkeletonBlock = styled.div`
  ${baseSkeleton};
  height: ${(p) => p.$h || '60px'};
  width: ${(p) => p.$w || '100%'};
  border-radius: ${(p) => p.$radius || '12px'};
`

const CardWrap = styled.div`
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  padding: 18px 20px;
  margin-bottom: 12px;
`

export function SkeletonCard({ lines = 2 }) {
  return (
    <CardWrap aria-hidden="true">
      <SkeletonLine $h="14px" $w="40%" $mb="10px" />
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine key={i} $h="11px" $w={`${85 - i * 10}%`} $mb={i === lines - 1 ? '0' : '6px'} />
      ))}
    </CardWrap>
  )
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export function SkeletonList({ count = 4, lines = 2 }) {
  return (
    <Stack aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} lines={lines} />
      ))}
    </Stack>
  )
}

const TodayHero = styled.div`
  background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 20px;
  padding: 28px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 18px;
`

const Circle = styled(SkeletonBlock)`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
`

export function TodaySkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading Today">
      <TodayHero>
        <Circle />
        <div style={{ flex: 1 }}>
          <SkeletonLine $h="14px" $w="30%" $mb="8px" />
          <SkeletonLine $h="22px" $w="60%" $mb="6px" />
          <SkeletonLine $h="12px" $w="80%" $mb="0" />
        </div>
      </TodayHero>
      <SkeletonCard lines={3} />
      <SkeletonCard lines={2} />
      <SkeletonCard lines={2} />
    </div>
  )
}

// ───────────────── per-page bespoke skeletons ─────────────────
//
// Each shape mirrors the live page's content silhouette so the perceived
// load is "the page is already here, content's coming" rather than the
// generic spinner-on-blank that we used to ship. Generic SkeletonList /
// SkeletonCard remain available for ad-hoc use; these are tighter shapes
// for the high-traffic pages.

const Pill = styled(SkeletonBlock)`
  width: 64px;
  height: 22px;
  border-radius: 999px;
  flex-shrink: 0;
`

const SmallCircle = styled(SkeletonBlock)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
`

const ProgressTrack = styled(SkeletonBlock)`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  margin-top: 10px;
`

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

/**
 * Vitals page: stack of metric tiles, each with a tiny sparkline rect on
 * the right and a latest-value line below the title.
 */
export function VitalsSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading vitals">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow style={{ justifyContent: 'space-between' }}>
            <SkeletonLine $h="14px" $w="35%" $mb="0" />
            <SkeletonBlock $h="28px" $w="80px" $radius="6px" />
          </InlineRow>
          <SkeletonLine $h="22px" $w="40%" $mb="0" style={{ marginTop: 12 }} />
          <SkeletonLine $h="11px" $w="55%" $mb="0" style={{ marginTop: 6 }} />
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Medicines page: each card has a name + dosage + a row of pill-shaped
 * "due time" chips along the bottom. Skeleton mirrors that layout.
 */
export function MedicinesSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading medicines">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow>
            <SmallCircle />
            <div style={{ flex: 1 }}>
              <SkeletonLine $h="15px" $w="55%" $mb="6px" />
              <SkeletonLine $h="11px" $w="40%" $mb="0" />
            </div>
          </InlineRow>
          <InlineRow style={{ marginTop: 12, gap: 8 }}>
            <Pill />
            <Pill style={{ width: 52 }} />
            <Pill style={{ width: 70 }} />
          </InlineRow>
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Diet page: a hero "today's plan" card up top, then a few meal-log cards.
 */
export function DietSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading diet">
      <CardWrap style={{ padding: '24px' }}>
        <SkeletonLine $h="13px" $w="25%" $mb="10px" />
        <SkeletonLine $h="20px" $w="65%" $mb="10px" />
        <SkeletonLine $h="11px" $w="80%" $mb="6px" />
        <SkeletonLine $h="11px" $w="45%" $mb="0" />
      </CardWrap>
      {Array.from({ length: 3 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow style={{ justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <SkeletonLine $h="13px" $w="30%" $mb="6px" />
              <SkeletonLine $h="11px" $w="50%" $mb="0" />
            </div>
            <Pill />
          </InlineRow>
        </CardWrap>
      ))}
    </div>
  )
}

/**
 * Goals page: each goal row has title + target + a horizontal progress
 * track. Skeleton mirrors with a fake progress bar at the bottom.
 */
export function GoalsSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading goals">
      {Array.from({ length: 3 }).map((_, i) => (
        <CardWrap key={i}>
          <SkeletonLine $h="14px" $w="60%" $mb="6px" />
          <SkeletonLine $h="11px" $w="40%" $mb="0" />
          <ProgressTrack />
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Activities page: each row is a small task card with title and one
 * descriptive line.
 */
export function ActivitiesSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading activities">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardWrap key={i}>
          <SkeletonLine $h="14px" $w="50%" $mb="6px" />
          <SkeletonLine $h="11px" $w={`${75 - i * 5}%`} $mb="0" />
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Practitioners list: each card is an avatar + name + specialty.
 */
export function PractitionersSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading practitioners">
      {Array.from({ length: 4 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow>
            <SkeletonBlock $h="44px" $w="44px" $radius="50%" />
            <div style={{ flex: 1 }}>
              <SkeletonLine $h="14px" $w="55%" $mb="6px" />
              <SkeletonLine $h="11px" $w="35%" $mb="0" />
            </div>
            <Pill />
          </InlineRow>
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Messages list: each row is small circle + (peer name + last-message
 * preview) + time-ago on the right.
 */
export function MessagesSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading messages">
      {Array.from({ length: 5 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow>
            <SmallCircle />
            <div style={{ flex: 1 }}>
              <SkeletonLine $h="13px" $w="40%" $mb="6px" />
              <SkeletonLine $h="11px" $w={`${85 - i * 7}%`} $mb="0" />
            </div>
            <SkeletonLine $h="10px" $w="40px" $mb="0" />
          </InlineRow>
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Shop / Packs: each card has a pack title + tier sub + a progress
 * track for sessions remaining.
 */
export function PacksSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading packs">
      {Array.from({ length: 2 }).map((_, i) => (
        <CardWrap key={i}>
          <SkeletonLine $h="15px" $w="55%" $mb="6px" />
          <SkeletonLine $h="11px" $w="40%" $mb="0" />
          <ProgressTrack />
          <InlineRow style={{ justifyContent: 'space-between', marginTop: 10 }}>
            <SkeletonLine $h="10px" $w="20%" $mb="0" />
            <SkeletonLine $h="10px" $w="25%" $mb="0" />
          </InlineRow>
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Subscription page: a single hero card (status pill + plan + renewal
 * info), no list.
 */
export function SubscriptionSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading subscription">
      <CardWrap style={{ padding: '24px' }}>
        <Pill style={{ marginBottom: 14 }} />
        <SkeletonLine $h="22px" $w="55%" $mb="10px" />
        <SkeletonLine $h="11px" $w="80%" $mb="6px" />
        <SkeletonLine $h="11px" $w="60%" $mb="6px" />
        <SkeletonLine $h="11px" $w="45%" $mb="0" />
      </CardWrap>
    </div>
  )
}

/**
 * Dues page: each row is a description + amount on the right.
 */
export function DuesSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading dues">
      {Array.from({ length: 3 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow style={{ justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <SkeletonLine $h="14px" $w="60%" $mb="6px" />
              <SkeletonLine $h="11px" $w="40%" $mb="0" />
            </div>
            <SkeletonLine $h="18px" $w="80px" $mb="0" />
          </InlineRow>
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Notifications center: each row mirrors the live shape — kind Pill +
 * time-ago + title + body line.
 */
export function NotificationsSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading notifications">
      {Array.from({ length: 5 }).map((_, i) => (
        <CardWrap key={i}>
          <InlineRow style={{ marginBottom: 8 }}>
            <Pill style={{ width: 56 }} />
            <SkeletonLine $h="10px" $w="60px" $mb="0" />
          </InlineRow>
          <SkeletonLine $h="14px" $w="55%" $mb="6px" />
          <SkeletonLine $h="11px" $w={`${85 - i * 6}%`} $mb="0" />
        </CardWrap>
      ))}
    </Stack>
  )
}

/**
 * Correction-request list (under PersonalProfilePage): each row has a
 * status pill + summary line.
 */
export function CorrectionRequestsSkeleton() {
  return (
    <Stack aria-busy="true" aria-label="Loading correction requests" style={{ marginTop: 16 }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <CardWrap key={i} style={{ marginBottom: 0, padding: 12 }}>
          <InlineRow style={{ justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <SkeletonLine $h="13px" $w="55%" $mb="4px" />
              <SkeletonLine $h="11px" $w="35%" $mb="0" />
            </div>
            <Pill style={{ width: 70 }} />
          </InlineRow>
        </CardWrap>
      ))}
    </Stack>
  )
}
